import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Linkedin, Youtube, Instagram, Send, Github, ArrowUpRight, Mail,
  GraduationCap, Radio, Users, MapPin,
} from 'lucide-react';
import { subscribeToQoreTalks } from '../../services/qoreTalksApi';
import '../../styles/footer.css';

const Footer = () => {
  const { t } = useTranslation();
  const year = new Date().getFullYear();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('');
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(null);

  const socials = [
    { href: 'https://linkedin.com/company/qorenova', label: 'LinkedIn', Icon: Linkedin },
    { href: 'https://youtube.com/@qorenova', label: 'YouTube', Icon: Youtube },
    { href: 'https://x.com/qorenova', label: 'X', Icon: null },
    { href: 'https://instagram.com/qorenova', label: 'Instagram', Icon: Instagram },
    { href: 'https://t.me/qorenova', label: 'Telegram', Icon: Send },
    { href: 'https://github.com/qorenova', label: 'GitHub', Icon: Github },
  ];

  const worlds = [
    {
      id: 'academy',
      title: 'QoreAcademy',
      icon: GraduationCap,
      accent: 'amber',
      links: [
        { to: '/academy', label: t('footer.qoreAcademy') || 'Overview' },
        { to: '/courses', label: t('footer.coursesLink') || 'Courses' },
        { to: '/virtuallabs', label: t('footer.virtualLabs') || 'Virtual labs' },
        { to: '/aitutoring', label: t('footer.aiTutoring') || 'AI tutoring' },
        { to: '/studentportal', label: t('footer.studentPortal') || 'Student portal' },
      ],
    },
    {
      id: 'talks',
      title: 'QoreTalks',
      icon: Radio,
      accent: 'rose',
      links: [
        { to: '/qoretalks', label: t('footer.podcastEpisodes') || 'Episodes' },
        { to: '/qoretalks/live', label: t('footer.liveStreams') || 'Live rooms' },
        { to: '/qoretalks/apply', label: 'Be a guest' },
        { to: '/qoretalks/subscribe', label: t('nav.subscribe') || 'Subscribe' },
      ],
    },
    {
      id: 'connect',
      title: 'QoreConnect',
      icon: Users,
      accent: 'cyan',
      links: [
        { to: '/qoreconnect', label: t('footer.qoreConnect') || 'Community' },
        { to: '/qoreconnect/forum', label: t('footer.communityForum') || 'Forum' },
        { to: '/qoreconnect/mentorship', label: t('nav.mentorship') || 'Mentorship' },
        { to: '/qoreconnect/jobs', label: t('footer.jobBoard') || 'Jobs' },
      ],
    },
  ];

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    setStatus('');
    try {
      await subscribeToQoreTalks({ email: email.trim() });
      setStatus('ok');
      setEmail('');
    } catch (err) {
      setStatus(err.message || 'Could not subscribe');
    } finally {
      setBusy(false);
    }
  };

  return (
    <footer className="qn-foot">
      <div className="qn-foot__aurora" aria-hidden>
        <span />
        <span />
        <span />
      </div>
      <div className="qn-foot__grid-bg" aria-hidden />

      <div className="qn-foot__inner">
        <div className="qn-foot__hero">
          <Link to="/" className="qn-foot__brand">
            <img src="/images/logo/logo-qorenova.png" alt="" />
            <div>
              <strong>QoreNova</strong>
              <small>{t('footer.tagline') || 'Igniting innovation'}</small>
            </div>
          </Link>
          <div className="qn-foot__social">
            {socials.map((s) => (
              <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer" aria-label={s.label}>
                {s.Icon ? <s.Icon size={16} /> : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                  </svg>
                )}
              </a>
            ))}
          </div>
        </div>

        <div className="qn-foot__worlds">
          {worlds.map((world) => {
            const Icon = world.icon;
            const expanded = open === world.id;
            return (
              <div key={world.id} className={`qn-foot__col qn-foot__col--${world.accent}`}>
                <button
                  type="button"
                  className="qn-foot__col-h"
                  onClick={() => setOpen(expanded ? null : world.id)}
                >
                  <Icon size={16} /> {world.title}
                </button>
                <nav className={expanded ? 'is-open' : ''}>
                  {world.links.map((l) => (
                    <Link key={l.to} to={l.to}>
                      {l.label} <ArrowUpRight size={12} />
                    </Link>
                  ))}
                </nav>
              </div>
            );
          })}
        </div>

        <div className="qn-foot__signal">
          <h3><Mail size={16} /> {t('footer.stayConnected') || 'Stay in the signal'}</h3>
          <form onSubmit={handleSubscribe}>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('footer.emailPlaceholder') || 'you@company.com'}
              disabled={busy}
            />
            <button type="submit" disabled={busy}>{busy ? '…' : 'Join'}</button>
          </form>
          {status === 'ok' && <div className="qn-foot__ok">{t('footer.subscribeSuccess') || 'You’re on the list.'}</div>}
          {status && status !== 'ok' && <div className="qn-foot__err">{status}</div>}
          <div className="qn-foot__meta">
            <span><Mail size={13} /> hello@qorenova.org</span>
            <span><MapPin size={13} /> {t('footer.location') || 'Global / Remote'}</span>
          </div>
        </div>
      </div>

      <div className="qn-foot__base">
        <span>© {year} QoreNova · {t('footer.rightsReserved') || 'All rights reserved'}</span>
        <nav>
          <Link to="/privacy">{t('footer.privacyPolicy') || 'Privacy'}</Link>
          <Link to="/terms">{t('footer.termsOfService') || 'Terms'}</Link>
          <Link to="/contact">{t('footer.contact') || 'Contact'}</Link>
        </nav>
        <span className="qn-foot__pulse"><i /> Systems online</span>
      </div>
    </footer>
  );
};

export default Footer;
